import * as Sentry from '@sentry/react';
import { isNetworkFailure } from 'utils/sentryConfig';

/**
 * Read a failed checkout response's body for the customer-facing error (#613).
 * Only a JSON object is trusted. Anything else, such as a gateway's HTML page or
 * a function timeout's text, is reported to Sentry (truncated) and dropped, so
 * the caller falls back to its generic wording instead of showing it.
 * @param {Response} response - a non-ok fetch response
 * @param {string} endpoint - function name, for the Sentry report
 * @returns {Promise<Object>} the JSON error body, or {}
 */
export async function readErrorBody(response, endpoint) {
  const text = await response.text();
  if (!text.trim()) return {};
  try {
    const data = JSON.parse(text);
    if (data && typeof data === 'object' && !Array.isArray(data)) return data;
  } catch {
    // not JSON
  }
  Sentry.captureMessage(`Non-JSON error response from ${endpoint}`, {
    level: 'warning',
    tags: { endpoint, status: String(response.status) },
    extra: { body: text.slice(0, 500) },
  });
  return {};
}

/**
 * Build the Error thrown when a payOrder request comes back non-ok.
 *
 * Distinguishes a routine Clover gateway decline (`code: 'card_declined'`,
 * see payOrder.js) from any other payOrder failure (misconfiguration,
 * network/parse issues, etc.) by tagging the thrown Error with
 * `isCardDecline`. Callers use this to report a decline to Sentry at a lower
 * severity instead of alerting like an outage would. See #519.
 *
 * A `PICKUP_*` code is the server refusing a scheduled pickup time
 * (pickupWindow.js, #547) before charging; `isPickupRejection` lets the
 * checkout send the customer back to step 1 instead of showing a payment
 * failure.
 *
 * @param {number} status - HTTP status from the payOrder fetch response
 * `reasonCode` names the kind of decline (declineReason.js), so a wallet
 * payment can word it for the wallet; see walletFailureMessage (#620).
 *
 * @param {{error?: string, reason?: string, reasonCode?: string, code?: string, declineCode?: string, cloverStatus?: number}} [errorData]
 * @returns {Error & {isCardDecline: boolean, isPickupRejection: boolean, code?: string, reasonCode?: string, cloverStatus?: number}}
 */
export function buildPayOrderError(status, errorData = {}) {
  // `reason` is the server's customer-safe explanation of a decline (#558);
  // it beats the generic summary so the customer knows what to fix.
  const message =
    errorData.reason ||
    errorData.error ||
    `Payment failed (${status}): Please check your payment details and try again.`;

  const error = new Error(message);
  error.isCardDecline = errorData.code === 'card_declined';
  if (typeof errorData.declineCode === 'string') {
    error.declineCode = errorData.declineCode;
  }
  if (typeof errorData.reasonCode === 'string') {
    error.reasonCode = errorData.reasonCode;
  }
  error.isPickupRejection =
    typeof errorData.code === 'string' && errorData.code.startsWith('PICKUP_');
  if (typeof errorData.code === 'string') error.code = errorData.code;
  if (typeof errorData.cloverStatus === 'number') {
    error.cloverStatus = errorData.cloverStatus;
  }
  return error;
}

const WALLET_NAMES = { applePay: 'Apple Wallet', googlePay: 'Google Wallet' };

/**
 * The message to show for a failed Apple Pay or Google Pay payment (#620).
 *
 * A wallet sends the billing address saved with the card in the wallet. When
 * that address is out of date, the bank approves the charge and Clover's
 * address check then reverses it. The card wording ("check it and try again")
 * points at a postal code the customer never typed, so a wallet payment says
 * where the address lives and what to do. Every other failure keeps its
 * message.
 *
 * @param {Error & {reasonCode?: string}} error
 * @param {'applePay'|'googlePay'} walletType
 * @returns {string}
 */
export function walletFailureMessage(error, walletType) {
  const wallet = WALLET_NAMES[walletType];
  if (error?.reasonCode !== 'postal_code' || !wallet) return error.message;
  return (
    `The billing address for this card in ${wallet} doesn't match your bank's records. ` +
    `Update it in ${wallet} or use a different card. You won't be charged.`
  );
}

export const NETWORK_FAILURE_MESSAGE =
  "We couldn't reach our server. Check your connection and try again.";

/**
 * Replace a browser's "Load failed" / "Failed to fetch" with words a customer
 * can act on (#632). Whether the charge reached Clover is unknown at this
 * point, so the message promises nothing about it. Any other error is
 * returned as is.
 * @param {Error} error
 * @returns {Error & {isNetworkFailure?: boolean}}
 */
export function describeNetworkFailure(error) {
  if (!isNetworkFailure(error)) return error;
  return Object.assign(new Error(NETWORK_FAILURE_MESSAGE, { cause: error }), {
    isNetworkFailure: true,
  });
}
